import { useEffect, useState } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, ShieldAlert, Clock, CheckCircle2, RefreshCw, Filter, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

export default function Compliance({ user }) {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');

  useEffect(() => { loadData(); }, []);

  const loadData = () => {
    setLoading(true);
    api.get('/compliance/alerts')
      .then(res => { setAlerts(res.data.alerts || []); setStats(res.data); })
      .catch(() => toast.error('Failed to load compliance data'))
      .finally(() => setLoading(false));
  };

  const filtered = alerts.filter(a => {
    if (filterType !== 'all' && a.entity !== filterType) return false;
    if (filterSeverity !== 'all' && a.severity !== filterSeverity) return false;
    return true;
  });

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Compliance Tracking</h1>
          <p className="text-slate-600 mt-1">Insurance expirations, authority issues, medical cards, and license alerts</p>
        </div>
        <button onClick={loadData} className="p-2 rounded-lg hover:bg-slate-100 transition-colors" data-testid="refresh-btn">
          <RefreshCw className="w-5 h-5 text-slate-600" />
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card data-testid="stat-total">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-slate-100"><ShieldAlert className="w-5 h-5 text-slate-600" /></div>
              <div><p className="text-sm text-slate-500">Total Alerts</p><p className="text-2xl font-bold">{stats.total || 0}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-critical">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
              <div><p className="text-sm text-slate-500">Critical (Expired)</p><p className="text-2xl font-bold text-red-700">{stats.critical || 0}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-warnings">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-100"><Clock className="w-5 h-5 text-amber-600" /></div>
              <div><p className="text-sm text-slate-500">Expiring (30 days)</p><p className="text-2xl font-bold text-amber-700">{stats.warnings || 0}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-clear">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100"><CheckCircle2 className="w-5 h-5 text-green-600" /></div>
              <div><p className="text-sm text-slate-500">Status</p><p className="text-2xl font-bold text-green-700">{stats.total === 0 ? 'All Clear' : 'Action Needed'}</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      {alerts.length > 0 && (
        <div className="flex items-center gap-3" data-testid="compliance-filters">
          <Filter className="w-4 h-4 text-slate-500" />
          <div className="flex gap-2">
            {['all', 'carrier', 'driver'].map(t => (
              <Button
                key={t}
                variant={filterType === t ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterType(t)}
                data-testid={`filter-type-${t}`}
              >
                {t === 'all' ? 'All Types' : t === 'carrier' ? 'Carriers' : 'Drivers'}
              </Button>
            ))}
          </div>
          <div className="w-px h-6 bg-slate-200" />
          <div className="flex gap-2">
            {['all', 'critical', 'warning'].map(s => (
              <Button
                key={s}
                variant={filterSeverity === s ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterSeverity(s)}
                data-testid={`filter-severity-${s}`}
              >
                {s === 'all' ? 'All' : s === 'critical' ? 'Critical' : 'Warnings'}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Alerts */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-lg font-medium text-slate-900">
              {alerts.length === 0 ? 'All Clear' : 'No matching alerts'}
            </p>
            <p className="text-sm text-slate-500">
              {alerts.length === 0 ? 'No compliance issues found. All insurance, medical cards, and licenses are up to date.' : 'Try changing your filters.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map((a, i) => (
            <Card key={i} className={a.severity === 'critical' ? 'border-red-200 bg-red-50/50' : 'border-amber-200 bg-amber-50/50'} data-testid={`alert-${i}`}>
              <CardContent className="py-3 flex items-center gap-4">
                <div className={`p-2 rounded-lg ${a.severity === 'critical' ? 'bg-red-100' : 'bg-amber-100'}`}>
                  {a.severity === 'critical' ? <AlertTriangle className="w-5 h-5 text-red-600" /> : <Clock className="w-5 h-5 text-amber-600" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-slate-900">{a.entity_name}</p>
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-200 text-slate-700 capitalize">{a.entity}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      a.field === 'Insurance' ? 'bg-blue-100 text-blue-700' :
                      a.field === 'Medical Card' ? 'bg-purple-100 text-purple-700' :
                      a.field === 'License' ? 'bg-indigo-100 text-indigo-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>{a.field}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${a.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{a.severity}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-0.5">{a.message}</p>
                </div>
                <div className="text-right shrink-0">
                  {a.days_until !== undefined && (
                    <p className={`text-lg font-bold ${a.days_until < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                      {a.days_until < 0 ? `${Math.abs(a.days_until)}d` : `${a.days_until}d`}
                    </p>
                  )}
                  <p className="text-xs text-slate-400">{a.days_until < 0 ? 'overdue' : 'remaining'}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
